/** Row-Shape der `marken`-Tabelle. */
export interface Marke {
  id: string;
  marken_name: string;
  rechtstraeger: string;
  business_unit: string | null;
  hrb_nr: string | null;
  ust_id: string | null;
  anschrift: string | null;
  iban: string | null;
  bic: string | null;
  logo_url: string | null;
  briefkopf_url: string | null;
  footer_disclaimer: string | null;
  absender_email: string | null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function buildFooterText(marke: Marke): string {
  const segments: string[] = [`${marke.marken_name} – eine Marke der ${marke.rechtstraeger}`];
  if (marke.anschrift) segments.push(marke.anschrift);
  if (marke.hrb_nr) segments.push(`HRB ${marke.hrb_nr}`);
  if (marke.ust_id) segments.push(`USt-ID ${marke.ust_id}`);
  return segments.join(' · ');
}

export function buildFooterHtml(marke: Marke): string {
  const text = escapeHtml(buildFooterText(marke));
  const disclaimer = marke.footer_disclaimer
    ? `<br/><span>${escapeHtml(marke.footer_disclaimer)}</span>`
    : '';
  return (
    `<hr style="border:none;border-top:1px solid #ccc;margin:1.5em 0;" />` +
    `<div style="font-size:0.85em;color:#666;line-height:1.4;">${text}${disclaimer}</div>`
  );
}

export function buildSignatureHtml(marke: Marke): string {
  const name = escapeHtml(marke.marken_name);
  const logo = marke.logo_url
    ? `<img src="${escapeHtml(marke.logo_url)}" alt="${name}" style="max-height:40px;display:block;margin-bottom:0.5em;" />`
    : '';
  return `<div style="margin-top:1.5em;">${logo}<strong>${name}</strong></div>`;
}

export function buildFromAddress(marke: Marke, fallbackEmail: string): string {
  const email = marke.absender_email || fallbackEmail;
  return `${marke.marken_name} - ${marke.rechtstraeger} <${email}>`;
}

export function applyBrandingToHtml(bodyHtml: string, marke: Marke): string {
  return `${bodyHtml}\n${buildSignatureHtml(marke)}\n${buildFooterHtml(marke)}`;
}

export function applyBrandingToText(bodyText: string, marke: Marke): string {
  return `${bodyText}\n\n-- \n${marke.marken_name}\n\n${buildFooterText(marke)}`;
}

const BUSINESS_UNIT_TO_MARKEN_NAME: Record<string, string> = {
  mietwagen: 'MietGo',
  mietgo: 'MietGo',
  autoglas: 'Lenneglas',
  gutachten: 'SV Büro Koop',
  werkstatt: 'LCS GmbH',
};

export function resolveMarkenNameForBusinessUnit(businessUnit: string | null | undefined): string | null {
  if (!businessUnit) return null;
  return BUSINESS_UNIT_TO_MARKEN_NAME[businessUnit.toLowerCase()] ?? null;
}
